import React from 'react';
import {
  colors,
  Grid,
  Typography,
  withStyles,
  createStyles,
  Theme,
  Link,
} from '@material-ui/core';
import { LanguageSelector } from 'app/components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import EqualHousingIcon from '../../assets/img/broker/EqualHousingLender.svg';
import FdicIcon from '../../assets/img/icons/fdic.png';
import { ClientType } from '@lenderful/domain';

const styles = (theme: Theme) =>
  createStyles({
    modalBody: {
      [theme.breakpoints.down('sm')]: {
        fontSize: 14,
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: 12,
      },
    },
    modalContent: {
      '& a': {
        color: theme.palette.primary.main,
        '&:hover': {
          color: theme.palette.primary.dark,
        },
      },
      '& table': {
        width: '100%',
      },
      '& th': {
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 500,
        padding: '6px',
      },
      '& td': {
        padding: '6px 10px',
      },
      '& tr': {
        '&:nth-child(2n)': {
          background: colors.grey[200],
        },
      },
      '& img': {
        display: 'none',
      },
      '& h1, h2': {
        lineHeight: 1.5,
      },
      '& h1': {
        [theme.breakpoints.down('xs')]: {
          fontSize: 20,
        },
      },
    },
    scroll: {
      overflowY: 'scroll',
    },
    icon: {
      margin: '16px 14px',
    },
  });

interface Props {
  links             : LinkInfo[];
  clientName        : string;
  clientType        : ClientType;
  classes           : any;
  isMultiLangEnabled: boolean;
}

interface LinkInfo {
  title  : string;
  content: string;
  url    : string;
}

const FooterComponent = ({
  links = [{ title: '', content: '', url: '' }],
  clientName,
  clientType,
  classes,
  isMultiLangEnabled,
}: Props) => {
  const { t }       = useTranslation();
  const currentYear = new Date().getFullYear();
  const copyright   = '\u00A9';

  const renderFooterLinks = (item: LinkInfo) => {
    const style = {
      fontSize: 14,
      padding: '0px 15px',
    };
    if (item.url) { // has url, use link
      return (
        <Link
          underline='hover'
          href={item.url}
          rel='noopener'
          target='_blank'
          style={style}
          children={item.title}
        />
      );
    }
    return (
      <Typography variant='body2' style={style}>
        {item.title}
      </Typography>
    );
  };

  const renderFooterItems = (item: LinkInfo) => {
    return renderFooterLinks(item);
  };

  return (
    <footer>
      <Grid container classes={{ container: 'wideGrid' }}>
        <Grid container justifyContent="center" alignItems="center">
          {
            clientType === ClientType.BANK && (
              <Grid item>
                <img className={classes.icon} src={FdicIcon} alt="FDIC icon" />
              </Grid>
            )
          }
          <Grid item>
            <img className={classes.icon} src={`${window.location.origin}${EqualHousingIcon}`} alt="Equal Housing Lender" />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {isMultiLangEnabled && <LanguageSelector footerSelector={true} />}
          <Grid container justifyContent="center" classes={{ container: 'footerItemContainer' }}>
            {links.map((item, index) => (
              <Grid item key={index}>
                {renderFooterItems(item)}
              </Grid>
            ))}
          </Grid>
          <Grid
            container
            item
            xs      = {12}
            justifyContent="center"
            classes = {{ container: 'footerTextContainer' }}
          >
            <Typography align="center" variant="body2" classes={{ body2: 'small' }}>
              {t('global:footer.copyright', {
                clientName,
                copyright,
                currentYear,
                defaultValue:
                  '{{ copyright }} {{ currentYear }} {{ clientName }}. All rights reserved. Mortgage services provided by {{ clientName }}.',
              })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </footer>
  );
};

export const Footer = withStyles(styles)(FooterComponent);
